<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import useAggregation from '../../use-aggregation';
import { useCategories } from '@ilteducation/content-ref-data';
import { useDisplay } from 'vuetify';
import FilterOptions from './Filters/FilterOptions.vue';
import type { MultipleFilterOptions, FilterOption, MultipleField, Field } from '../../search-panel-types';
import { useSearchFilterStore } from '../../use-search-filter';
import FilterRangeAndMultiple from './Filters/FilterRangeAndMultiple.vue';
import FilterMultipleSwitches from './Filters/FilterMultipleSwitches.vue';
import useFormatFilterOptions from '../../composables/use-format-filter-options';

type Props = {
  filterTop?: number;
  currentSubscription: string;
  minAge?: number;
  maxAge?: number;
};
const props = defineProps<Props>();

const aggregateStore = useAggregation();
const { result, isLoading } = storeToRefs(aggregateStore);
const { load: loadCategories } = useCategories();

const searchFilterStore = useSearchFilterStore();
const { updateSelectedFilters, removeSelectedFilterByValue, getAgeFilterItems } = searchFilterStore;
const { fields } = storeToRefs(searchFilterStore);
const { selectedFilters } = storeToRefs(searchFilterStore);
const { mdAndUp } = useDisplay();
const { transformToFilterOptions } = useFormatFilterOptions();

// Defines amount of opened expand panels by default
const expansionPanel = ref([]);

const filterContainer = ref(null);

const topFilter = computed(() => props.filterTop || 0);

const heightValue = computed(() => {
  return mdAndUp.value ? `calc(100vh - ${topFilter.value}px)` : 'auto';
});

const getCategories = async (): Promise<void> => {
  await loadCategories(props.currentSubscription);
};

onMounted(async () => {
  await getCategories();
});

const getFilterOptions = (field: Field): FilterOption[] => {
  const currentField = result.value.find((i) => i.field === field.aggregationKey);
  if (!currentField) {
    return [];
  }
  if (field.aggregationKey === 'ages' && typeof props.minAge !== 'undefined' && typeof props.maxAge !== 'undefined') {
    return transformToFilterOptions(field, getAgeFilterItems(props.minAge, props.maxAge));
  }
  return transformToFilterOptions(field, currentField.counts);
};

const getGroupedFilterOptions = (field: MultipleField): MultipleFilterOptions[] =>
  field.fields.reduce(
    (options: MultipleFilterOptions[], singleField) => [
      ...options,
      {
        field: singleField,
        options: getFilterOptions(singleField),
      },
    ],
    [] as MultipleFilterOptions[],
  );
</script>

<template>
  <div ref="filterContainer" class="filters-container" :style="`top: ${topFilter}px;`">
    <v-expansion-panels v-if="result.length > 0" v-model="expansionPanel" multiple>
      <template v-for="(field, index) in fields">
        <FilterMultipleSwitches
          v-if="field.type === 'multiple_switches'"
          :key="field.label"
          :filters="getGroupedFilterOptions(field as MultipleField)"
          :label="field.label"
          :selected="selectedFilters"
          @filters-updated="updateSelectedFilters"
          @filter-removed="removeSelectedFilterByValue"
        ></FilterMultipleSwitches>
        <FilterRangeAndMultiple
          v-else-if="field.type === 'multiple_predefined_ranges'"
          :key="'multiple_predefined_ranges' + index"
          :items="getFilterOptions(field)"
          :field-key="field.aggregationKey"
          :label="field.label"
          :ranges="field.ranges ?? []"
          :selected="selectedFilters"
          @filters-updated="updateSelectedFilters"
        />
        <FilterOptions
          v-else-if="field.type === 'list'"
          :key="'options' + index"
          :field-key="field.aggregationKey"
          :items="getFilterOptions(field)"
          :selected="selectedFilters"
          :label="field.label"
          @filters-updated="updateSelectedFilters"
          @filter-removed="removeSelectedFilterByValue"
        />
      </template>
      <v-overlay class="h-100" :opacity="0.2" :value="isLoading || result.length === 0" position="absolute">
        <v-progress-circular :size="70" :width="7" color="primary" indeterminate></v-progress-circular>
      </v-overlay>
    </v-expansion-panels>
  </div>
</template>

<style scoped lang="scss">
@import 'vuetify/settings';

.v-expansion-panels {
  justify-content: flex-start;
}
.filters-container {
  position: sticky;
  overflow-y: auto;
  width: 100%;
  top: v-bind(topFilter);
  height: v-bind(heightValue);
  @media #{map-get($display-breakpoints, 'md-and-up')} {
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
  }
}
@media #{map-get($display-breakpoints, 'md-and-up')} {
  /* Chrome, Safari and Opera */
  .filters-container::-webkit-scrollbar {
    display: none;
  }

  .filters-container::-webkit-scrollbar-track {
    scrollbar-width: thin; /* Firefox */
    background-color: rgb(var(--v-theme-surface-variant));
  }

  .filters-container::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: rgba(0, 0, 0, 0.3);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .filters-container::-webkit-scrollbar-thumb:hover {
    background-color: rgba(0, 0, 0, 0.4);
  }
}
</style>
